import Icon from '@atoms/Icon/Icon';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import type { IImage } from '@models/IImage';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import classNames from 'classnames';
import { type PropsWithChildren, type RefObject, useState } from 'react';

interface Props {
  readonly ariaHidden?: boolean;
  readonly classes?: string;
  readonly dataTestId?: string;
  readonly epiEdit?: string;
  readonly fallbackSize?: string;
  readonly image?: IImage;
  readonly imageClasses?: string;
  readonly imageRef?:
    | RefObject<HTMLImageElement>
    | ((node: HTMLElement | null) => void);
  readonly lazyLoad?: boolean;
  readonly progressiveLoad?: boolean;
  readonly showFallback?: boolean;
  readonly sizes?: string;
}

const Image = ({
  ariaHidden,
  children,
  classes,
  dataTestId,
  epiEdit,
  fallbackSize,
  image,
  imageClasses,
  imageRef,
  lazyLoad = true,
  progressiveLoad = true,
  showFallback,
  sizes = '',
}: PropsWithChildren<Props>) => {
  const mobileSource = image?.mobile;
  const tabletSource = image?.tablet;
  const desktopSource = image?.desktop;
  const largeDesktopSource = image?.largeDesktop;
  const breakpoint = useBreakpoint() as BreakpointEnum;
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  if (!desktopSource?.url && !showFallback) return null;

  const currentSrc = {
    [BreakpointEnum.Small]: mobileSource,
    [BreakpointEnum.Medium]: tabletSource,
    [BreakpointEnum.Large]: desktopSource,
    [BreakpointEnum.XLarge]: largeDesktopSource,
  };

  if (showFallback && !desktopSource?.url) {
    return (
      <div className={classNames(classes, 'flex items-center justify-center')}>
        <Icon
          ariaHidden={true}
          className={classNames(
            imageClasses,
            'motion-safe:transition motion-safe:duration-500 motion-safe:ease-in-out'
          )}
          name="image-outline"
          size={fallbackSize ?? '100%'}
        />
        {children}
      </div>
    );
  }

  return (
    <picture className={classNames(classes)}>
      <source
        type="image/webp"
        sizes={sizes}
        srcSet={`${mobileSource?.url} 376w, ${tabletSource?.url} 768w, ${desktopSource?.url} 1024w, ${largeDesktopSource?.url} 1440w`}
      />

      <img
        ref={imageRef}
        alt={image?.altText?.length ? image?.altText : 'nikon-image'}
        aria-hidden={ariaHidden}
        className={classNames(
          imageClasses,
          'motion-safe:transition motion-safe:duration-500 motion-safe:ease-in-out'
        )}
        data-epi-property-name={epiEdit}
        draggable="false"
        loading={lazyLoad ? 'lazy' : 'eager'}
        onLoad={() => {
          setIsLoaded(true);
        }}
        sizes={sizes}
        src={
          isLoaded && progressiveLoad
            ? currentSrc[breakpoint]?.url
            : mobileSource?.url
        }
        height={currentSrc[breakpoint]?.height}
        width={currentSrc[breakpoint]?.width}
        srcSet={`${mobileSource?.originalUrl} 376w, ${tabletSource?.originalUrl} 768w, ${desktopSource?.originalUrl} 1024w, ${largeDesktopSource?.originalUrl} 1440w`}
        {...(dataTestId && { 'data-testid': dataTestId })}
      />
      {isLoaded && children}
    </picture>
  );
};

export default Image;
